import React, { useState } from "react";
import Switch from "@/component/Switch";
import AskIcon from "@/assets/share/ic_ask.svg";
import IcArrowRight from "@/assets/common/ic_arrow_right.svg?react";
import ActionSheet from "../ActionSheet";
import Tooltip from "@/component/Tooltip";
import { LinkValidPeriodType, LinkValidTextMap } from "@/constants/share";
import IcRadioActive from '@/assets/share/ic_radio_active.svg?react';
import IcRadioNormal from '@/assets/share/ic_radio_normal.svg?react';
import dayjs from "dayjs";

interface SecuritySheetProps {
  visible: boolean;
  expiredTime?: Date;
  onClose?: () => void;
  linkValidity?: string;
  onLinkValidityChange: (value: string) => void;
  allowBackUp: boolean;
  onAllowBackUpChange: (checked: boolean) => void;
  allowExport: boolean;
  onAllowExportChange: (checked: boolean) => void;
  watermarkEnable: boolean;
  onWatermarkEnableChange: (checked: boolean) => void;
}

type ValidityOption = {
  id: string;
  label: string;
};

const SecuritySheet: React.FC<SecuritySheetProps> = ({
  visible,
  expiredTime,
  onClose,
  linkValidity,
  onLinkValidityChange,
  allowBackUp,
  onAllowBackUpChange,
  allowExport,
  onAllowExportChange,
  watermarkEnable,
  onWatermarkEnableChange,
}) => {
  const [step, setStep] = useState<1 | 2>(1);

  const linkValidityOptions: ValidityOption[] = [
    { id: LinkValidPeriodType.Permanent, label: LinkValidTextMap[LinkValidPeriodType.Permanent] },
    { id: LinkValidPeriodType.OneWeek, label: LinkValidTextMap[LinkValidPeriodType.OneWeek] },
    { id: LinkValidPeriodType.TwoWeek, label: LinkValidTextMap[LinkValidPeriodType.TwoWeek] },
    { id: LinkValidPeriodType.OneMonth, label: LinkValidTextMap[LinkValidPeriodType.OneMonth] },
  ];

  const buildTitle = (title: string) => <span className="text-size-3 text-#626773 mb-2">{title}</span>;

  const buildSwitchItem = (content: string, checked: boolean, onChange: (check: boolean) => void) => {
    return (
      <div className="bg-white rounded-3 p-4 flex items-center justify-between mb-6">
        <span className="text-size-3.5 text-#191D26">{content}</span>
        <Switch checked={checked} onChange={onChange} />
      </div>
    );
  }

  const onCloseHandle = () => {
    if (step == 1) {
      onClose?.();
    } else {
      setStep(1);
    }
  }

  const days = (linkValidity && linkValidity !== LinkValidPeriodType.Permanent) ?
    dayjs(expiredTime).format('MM-DD HH:mm') :
    undefined;

  return (
    <ActionSheet
      title={step == 1 ? "安全设置" : "链接有效期"}
      visible={visible}
      showReturn={true}
      onClose={onCloseHandle}
      className="h-133 bg-#F4F6F9!"
    >
      {step == 1 ?
        <div className="p-6 flex flex-col">
          {/* 链接有效期 */}
          {buildTitle("链接有效期")}
          <div className="bg-white rounded-3 p-4 flex items-center justify-between mb-6">
            <span className="text-size-3.5 text-#191D26">{(LinkValidTextMap[linkValidity ?? LinkValidPeriodType.Permanent]) + (days ? `有效（${days}）` : "")}</span>
            <div className="flex items-center" onClick={() => { setStep(2); }}>
              <span className=" text-#A1A7B2 text-3.5">修改</span>
              <IcArrowRight className="w-5.5 h-5.5 ml-2" />
            </div>
          </div>
          {/* 创建副本 */}
          {buildTitle("创建副本")}
          <div className="bg-white rounded-3 p-4 flex items-center justify-between mb-6">
            <div className="flex items-center">
              <span className="text-size-3.5 text-#191D26">允许成员创建副本</span>
              <Tooltip
                content={<span>被分享人可对当前会记创建副<br />本，并对副本进行编辑操作</span>}>
                <img className="ml-2 w-5 h-5" src={AskIcon} />
              </Tooltip>
            </div>
            <Switch checked={allowBackUp} onChange={onAllowBackUpChange} />
          </div>

          {/* 导出文件 */}
          {buildTitle("导出文件")}
          {buildSwitchItem("允许成员导出", allowExport, onAllowExportChange)}

          {/* 水印 */}
          {buildTitle("水印")}
          {buildSwitchItem("导出文件不带水印", watermarkEnable, onWatermarkEnableChange)}
        </div>
        :
        <div className="flex flex-col m-6 bg-white rd-3 px-4">
          {linkValidityOptions.map((option, index) => (
            <div
              key={option.id}
              className={`flex items-center justify-between h-12.5 cursor-pointer ${index < linkValidityOptions.length - 1 ? "b-0 b-solid b-b-1 b-#F4F6F9" : ""}`}
              onClick={() => onLinkValidityChange(option.id)}
            >
              <span className="text-#191D26  text-3.5">{option.label}</span>
              {linkValidity === option.id ? (
                <IcRadioActive className="w-4 h-4" />
              ) : (
                <IcRadioNormal className="w-4 h-4" />
              )}
            </div>
          ))}
        </div>}
    </ActionSheet>
  );
};

export default SecuritySheet;
